import axios from 'axios';
import { normalizeProgramEnrollmentRows } from '../../utils/dataNormalizer';
import { PROGRAMS } from '../../constants/programs';

const decorateNormalized = (normalized, meta) => {
    if (Array.isArray(normalized)) {
        normalized.meta = { ...(normalized.meta || {}), ...meta };
    }
    return normalized;
};

const canonicalErrorMessage = (error) =>
    error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || 'Unknown error';

const canonicalErrorDetails = (error) => ({
    code:
        error?.response?.data?.error?.code ||
        error?.response?.data?.code ||
        error?.code ||
        'CANONICAL_HISTORY_REQUEST_FAILED',
    status: error?.response?.status || null,
    message: canonicalErrorMessage(error),
});

const normalizeCanonicalSnapshot = (data, programName) => {
    const normalized = normalizeProgramEnrollmentRows({
        programCd: data.programCode,
        programName: data.programName || programName,
        enrollmentReports: data.enrollmentReports || [],
    });
    return decorateNormalized(normalized, {
        programName: data.programName || programName,
        programCode: data.programCode || null,
        cachedAt: data.sourceCachedAt || null,
        snapshotCapturedAt: data.sourceCachedAt || null,
        requestedDate: data.requestedDate || data.businessDate || null,
        businessDate: data.businessDate || null,
        source: data.source || 'canonical_daily_archive',
        selection: data.selection || 'exact',
        isFinalized: Boolean(data.isFinalized),
        corrected: Boolean(data.corrected),
    });
};

const getCanonicalProgramSnapshot = async (programName, snapshotDate) => {
    const { data } = await axios.get(
        `/api/program-history/v2/${encodeURIComponent(programName)}/snapshots/${encodeURIComponent(snapshotDate)}`
    );
    return normalizeCanonicalSnapshot(data, programName);
};

const normalizeSnapshotBac = (value) => {
    const digits = String(value ?? '').trim();
    if (!/^\d{1,6}$/.test(digits)) {
        const error = new Error('BAC must contain one to six digits.');
        error.code = 'INVALID_BAC';
        throw error;
    }
    return digits.padStart(6, '0');
};

const getEffectiveParams = (options = {}) => {
    const params = {};
    if (options.snapshotDate) {
        params.snapshotDate = options.snapshotDate;
    }
    return Object.keys(params).length > 0 ? params : undefined;
};

export const getEnrollmentByProgram = async (programName, options = {}) => {
    if (options.snapshotDate) {
        return getCanonicalProgramSnapshot(programName, options.snapshotDate);
    }
    const params = getEffectiveParams(options);
    const { data } = await axios.get(`/api/enrollment-report/program/${programName}`, { params });
    const normalized = normalizeProgramEnrollmentRows(data);
    return decorateNormalized(normalized, {
        programName,
        programCode: data.programCd || null,
        cachedAt: data.cachedAt || null,
        snapshotCapturedAt: data.snapshotCapturedAt || null,
        requestedDate: options.snapshotDate || null,
        source: data.source || 'live',
    });
};

export const getEnrollmentForAllPrograms = async (options = {}) => {
    const { snapshotDate } = options;
    if (!snapshotDate) {
        const promises = PROGRAMS.map((programName) =>
            axios.get(`/api/enrollment-report/program/${programName}`).then((response) => ({
                programName,
                data: decorateNormalized(normalizeProgramEnrollmentRows(response.data), {
                    programName,
                    programCode: response.data.programCd || null,
                    cachedAt: response.data.cachedAt || null,
                    snapshotCapturedAt: response.data.snapshotCapturedAt || null,
                    requestedDate: null,
                    source: response.data.source || 'live',
                }),
            }))
        );
        const responses = await Promise.all(promises);
        return responses;
    }

    const requests = PROGRAMS.map((programName) =>
        getCanonicalProgramSnapshot(programName, snapshotDate)
            .then((data) => ({ programName, data }))
    );

    const settled = await Promise.allSettled(requests);
    const successes = [];
    const failures = [];

    settled.forEach((result, index) => {
        const programName = PROGRAMS[index];
        if (result.status === 'fulfilled') {
            successes.push(result.value);
        } else {
            failures.push({ programName, ...canonicalErrorDetails(result.reason) });
        }
    });

    if (failures.length > 0) {
        const aggregateError = new Error('Some program snapshots were unavailable for the requested date.');
        aggregateError.code = 'SNAPSHOT_PARTIAL';
        aggregateError.programErrors = failures;
        aggregateError.partialResults = successes;
        throw aggregateError;
    }

    return successes;
};

export const getSnapshots = async () => {
    const { data } = await axios.get('/api/snapshots');
    return data;
};

export const fetchProgramSnapshot = async (programName, snapshotDate) => {
    try {
        return await getEnrollmentByProgram(programName, { snapshotDate });
    } catch (error) {
        const details = canonicalErrorDetails(error);
        if (details.status === 404) details.code = 'SNAPSHOT_NOT_FOUND';
        const friendly = new Error(details.message);
        friendly.code = details.code;
        friendly.status = details.status;
        friendly.programErrors = [{ programName, ...details }];
        throw friendly;
    }
};

export const fetchAllSnapshot = (snapshotDate) => getEnrollmentForAllPrograms({ snapshotDate });

/** Load only the requested BAC from each exact daily program archive. */
export const fetchDealerSnapshot = async (bac, snapshotDate) => {
    const normalizedBac = normalizeSnapshotBac(bac);
    const requests = PROGRAMS.map((programName) =>
        axios
            .get(
                `/api/program-history/v2/${encodeURIComponent(programName)}` +
                `/snapshots/${encodeURIComponent(snapshotDate)}/dealers/${normalizedBac}`
            )
            .then(({ data }) => ({ programName, data: normalizeCanonicalSnapshot(data, programName) }))
    );
    const settled = await Promise.allSettled(requests);
    const results = [];
    const failures = [];

    settled.forEach((result, index) => {
        const programName = PROGRAMS[index];
        if (result.status === 'fulfilled') {
            results.push(result.value);
            return;
        }
        const errorCode = result.reason?.response?.data?.error?.code;
        if (errorCode === 'DEALER_NOT_FOUND') return;
        failures.push({ programName, ...canonicalErrorDetails(result.reason) });
    });

    if (failures.length > 0) {
        const error = new Error('Some exact daily program snapshots were unavailable.');
        error.code = 'SNAPSHOT_PARTIAL';
        error.programErrors = failures;
        error.partialResults = results;
        throw error;
    }
    return results;
};
